// Kenya-only phone input with a flag-dropdown adornment.
// - Formats user input as "7XX XXX XXX" (strips non-digits, drops leading 0, caps at 9).
// - Toggles the country dropdown and closes on outside click / Escape.
// Raw digits (no +254) remain in the input value — the server's
// normalize_phone/1 adds the "254" prefix.

type PhoneHookThis = {
  el: HTMLElement
  _onInput?: (e: Event) => void
  _onToggle?: (e: Event) => void
  _onOutside?: (e: MouseEvent) => void
  _onKey?: (e: KeyboardEvent) => void
}

function sanitize(raw: string): string {
  return raw.replace(/\D/g, "").replace(/^0+/, "").slice(0, 9)
}

function format(digits: string): string {
  const p1 = digits.slice(0, 3)
  const p2 = digits.slice(3, 6)
  const p3 = digits.slice(6, 9)
  return [p1, p2, p3].filter(Boolean).join(" ")
}

export const PhoneInputKe = {
  mounted(this: PhoneHookThis) {
    const input = this.el.querySelector<HTMLInputElement>("input[data-phone-field]")
    const toggle = this.el.querySelector<HTMLButtonElement>("[data-phone-toggle]")
    const menu = this.el.querySelector<HTMLElement>("[data-phone-menu]")
    if (!input) return

    const applyFormat = (preserveCaret = true) => {
      const before = input.value
      const next = format(sanitize(before))
      if (next === before) return
      const caret = preserveCaret ? (input.selectionStart ?? next.length) : next.length
      const trailing = before.length - caret
      input.value = next
      const pos = Math.max(0, next.length - trailing)
      try {
        input.setSelectionRange(pos, pos)
      } catch {
        /* ignore */
      }
    }

    this._onInput = () => applyFormat(true)
    input.addEventListener("input", this._onInput)
    applyFormat(false)

    if (toggle && menu) {
      const openMenu = (open: boolean) => {
        menu.classList.toggle("hidden", !open)
        toggle.setAttribute("aria-expanded", String(open))
      }

      this._onToggle = (e) => {
        e.preventDefault()
        openMenu(menu.classList.contains("hidden"))
      }
      this._onOutside = (e) => {
        if (!this.el.contains(e.target as Node)) openMenu(false)
      }
      this._onKey = (e) => {
        if (e.key === "Escape") openMenu(false)
      }

      toggle.addEventListener("click", this._onToggle)
      document.addEventListener("click", this._onOutside)
      document.addEventListener("keydown", this._onKey)
    }
  },

  updated(this: PhoneHookThis) {
    const input = this.el.querySelector<HTMLInputElement>("input[data-phone-field]")
    if (!input) return
    const next = format(sanitize(input.value))
    if (input.value !== next) input.value = next
  },

  destroyed(this: PhoneHookThis) {
    if (this._onOutside) document.removeEventListener("click", this._onOutside)
    if (this._onKey) document.removeEventListener("keydown", this._onKey)
  },
}
